/**
 * <s-product-gallery> — PDP gallery: one large image + thumbnails, no library.
 *
 * - Track [data-gallery-track] is a native scroll-snap row (swipe on touch).
 * - Thumbnails [data-gallery-thumb] scroll the track; the visible slide marks its
 *   thumbnail active (aria-current + data-active) via IntersectionObserver.
 * - Desktop (fine pointer + hover): magnify inside the frame, following the
 *   pointer; the image's `sizes` is doubled on first hover so a sharp candidate loads.
 * - Every frame is a button that opens the existing full-screen dialog
 *   (commandfor/show-modal). Inside it, a tap toggles a 2.5× zoom panned by native
 *   scrolling; native pinch-zoom stays available.
 * - Follows variant changes: when the product-form partial re-renders with a new
 *   [data-variant-media-id], the matching slide is shown.
 *
 * Markup contract: [data-gallery-slide][data-media-id], [data-zoom-frame] wrapping
 * img[data-gallery-img], [data-gallery-thumb][data-index], dialog img[data-zoom-image].
 */
const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class SProductGallery extends HTMLElement {
  #ac;
  #observer;
  #formObserver;
  #slides = [];
  #thumbs = [];
  #track;
  #lastVariantMedia = '';

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.#track = this.querySelector('[data-gallery-track]');
    this.#slides = [...this.querySelectorAll('[data-gallery-slide]')];
    this.#thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
    if (!this.#track) return;

    this.#thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.index)), opts);
    });

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.#setActive(this.#slides.indexOf(entry.target));
        }
      },
      { root: this.#track, threshold: 0.6 },
    );
    this.#slides.forEach((slide) => this.#observer.observe(slide));

    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      this.querySelectorAll('[data-zoom-frame]').forEach((frame) => this.#bindHoverZoom(frame, opts));
    }

    this.querySelectorAll('dialog img[data-zoom-image]').forEach((img) => this.#bindTapZoom(img, opts));

    const form = document.querySelector('s-product-form[data-product-form]');
    if (form) {
      this.#lastVariantMedia = form.querySelector('[data-variant-media-id]')?.dataset.variantMediaId || '';
      this.#formObserver = new MutationObserver(() => this.#syncVariantMedia(form));
      this.#formObserver.observe(form, { childList: true, subtree: true });
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
    this.#formObserver?.disconnect();
  }

  goTo(index) {
    const slide = this.#slides[index];
    if (!slide) return;
    this.#track.scrollTo({ left: slide.offsetLeft - this.#track.offsetLeft, behavior: reduceMotion() ? 'auto' : 'smooth' });
    this.#setActive(index);
  }

  #setActive(index) {
    if (index < 0) return;
    this.#thumbs.forEach((thumb, i) => {
      const active = i === index;
      thumb.toggleAttribute('data-active', active);
      if (active) {
        thumb.setAttribute('aria-current', 'true');
        thumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
  }

  #syncVariantMedia(form) {
    const id = form.querySelector('[data-variant-media-id]')?.dataset.variantMediaId || '';
    if (!id || id === this.#lastVariantMedia) return;
    this.#lastVariantMedia = id;
    const index = this.#slides.findIndex((slide) => slide.dataset.mediaId === id);
    if (index >= 0) this.goTo(index);
  }

  #bindHoverZoom(frame, opts) {
    const img = frame.querySelector('img[data-gallery-img]');
    if (!img) return;
    frame.addEventListener(
      'pointerenter',
      () => {
        if (!img.dataset.sizesUpgraded) {
          img.dataset.sizesUpgraded = 'true';
          img.sizes = `${Math.round(frame.clientWidth * 2.2)}px`;
        }
        frame.setAttribute('data-zooming', '');
      },
      opts,
    );
    frame.addEventListener(
      'pointermove',
      (event) => {
        const rect = frame.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width) * 100;
        const y = ((event.clientY - rect.top) / rect.height) * 100;
        img.style.transformOrigin = `${x}% ${y}%`;
      },
      opts,
    );
    frame.addEventListener('pointerleave', () => frame.removeAttribute('data-zooming'), opts);
  }

  #bindTapZoom(img, opts) {
    const pan = img.closest('[data-zoom-pan]');
    const dialog = img.closest('dialog');
    if (!pan) return;
    img.addEventListener(
      'click',
      (event) => {
        const zoomed = pan.toggleAttribute('data-zoomed');
        img.setAttribute('aria-expanded', String(zoomed));
        if (!zoomed) return;
        // Keep the tapped point under the finger once the image is enlarged.
        const rect = img.getBoundingClientRect();
        const rx = (event.clientX - rect.left) / rect.width;
        const ry = (event.clientY - rect.top) / rect.height;
        requestAnimationFrame(() => {
          pan.scrollLeft = rx * pan.scrollWidth - pan.clientWidth / 2;
          pan.scrollTop = ry * pan.scrollHeight - pan.clientHeight / 2;
        });
      },
      opts,
    );
    dialog?.addEventListener('close', () => pan.removeAttribute('data-zoomed'), opts);
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
